import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Iter XX.44.C/E — Team Pulse (frontend).
 *
 * Aggregate-only, privacy-safe roster health for coaches. Every number
 * is banded (low/moderate/high) or trend-labelled (up/flat/down). No
 * individual player identity or activity ever surfaces.
 *
 * Language contract:
 *  • "engagement signal", "quiet-player count", "confidence signal"
 *  • Never "likely to leave", never causal claims.
 *  • Comparison period shown for every trend.
 */
export default function TeamPulse() {
  const { user, loading: authLoading } = useAuth();
  const { team_id } = useParams();
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(null);
  const [team, setTeam] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, prefRes] = await Promise.all([
        api.get(`/partners/me/teams/${team_id}/pulse`),
        api.get(`/partners/me/teams/${team_id}/digest/preferences`).catch(() => null),
      ]);
      setPulse(pRes.data.pulse);
      setTeam(pRes.data.team);
      if (prefRes) setPrefs(prefRes.data);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) setError("not_authorised");
      else if (code === 404) setError("team_not_found");
      else setError("load_failed");
    } finally { setLoading(false); }
  }, [team_id]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?return_to=/partner/team/${team_id}/pulse`); return; }
    load();
  }, [authLoading, user, load, navigate, team_id]);

  async function refresh() {
    setBusy(true);
    try {
      const { data } = await api.post(`/partners/me/teams/${team_id}/pulse/refresh`);
      setPulse(data.pulse);
      toast.success("Pulse refreshed");
    } catch { toast.error("Couldn't refresh"); }
    finally { setBusy(false); }
  }

  async function nudgeQuiet() {
    if (!window.confirm("Send Gully a gentle, private reflection nudge to every quiet player? They will never be told a coach initiated this.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/partners/me/teams/${team_id}/nudge-quiet`);
      toast.success(`${data.nudged || 0} private nudges sent`);
    } catch { toast.error("Couldn't send nudges"); }
    finally { setBusy(false); }
  }

  async function togglePref(field, value) {
    try {
      const next = { ...prefs, [field]: value };
      const { data } = await api.post(`/partners/me/teams/${team_id}/digest/preferences`, {
        enrolled: field === "enrolled" ? value : next.enrolled,
        channels: field === "channels" ? value : next.channels,
      });
      setPrefs(data);
      toast.success("Preferences saved");
    } catch { toast.error("Couldn't save"); }
  }

  if (authLoading || loading) return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">Loading Team Pulse…</div>;
  if (error === "not_authorised") return <ErrorBox>{"You aren't authorised to view this team's Pulse."}</ErrorBox>;
  if (error === "team_not_found") return <ErrorBox>Team not found.</ErrorBox>;
  if (error || !pulse) return <ErrorBox>{"Couldn't load Team Pulse."}</ErrorBox>;

  const suppressed = pulse.suppressed;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="team-pulse-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/partner" className="text-sm text-neutral-500 hover:text-neutral-300" data-testid="tp-back">← Dashboard</Link>
        <h1 className="text-3xl font-semibold mt-2 mb-1" data-testid="tp-title">Team Pulse</h1>
        <div className="text-neutral-400 mb-1">{team?.name}</div>
        {!suppressed && (
          <div className="text-xs text-neutral-500 mb-6">
            Week of {pulse.week_start} → {pulse.week_end} · {pulse.team_size} players
          </div>
        )}

        {suppressed ? (
          <div className="bg-neutral-900 border border-dashed border-neutral-800 rounded-xl p-8 text-center" data-testid="tp-suppressed">
            <div className="text-lg text-neutral-200 mb-2">Not enough data yet</div>
            <div className="text-sm text-neutral-500">{pulse.suppression_reason || "This team is below the privacy threshold this week."}</div>
            <div className="text-xs text-neutral-600 mt-4">
              Team Pulse suppresses everything until at least {pulse.privacy_floor?.k_anonymity_min ?? 5} players are on the roster. This keeps every player&apos;s activity truly anonymous.
            </div>
          </div>
        ) : (
          <>
            {/* Signal cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="tp-signals">
              <SignalCard
                title="Engagement signal"
                metric={`${pulse.active.count} / ${pulse.team_size} active`}
                band={pulse.active.band}
                trend={pulse.active.trend_vs_prev_week}
                comparePeriod="vs previous week"
                explainer="How many players opened Gully this week."
                testid="tp-signal-engagement"
              />
              <SignalCard
                title="Practice-consistency signal"
                metric=""
                band={pulse.practice_consistency.band}
                explainer="How many players reflected in Gully this week."
                testid="tp-signal-practice"
              />
              <SignalCard
                title="Video-review signal"
                metric=""
                band={pulse.video_review_usage.band}
                explainer="How many players used the video-review tool this week."
                testid="tp-signal-video"
              />
              <SignalCard
                title="Confidence signal"
                metric=""
                trend={pulse.confidence_trend?.trend === "insufficient_data" ? null : pulse.confidence_trend?.trend}
                comparePeriod="vs previous week"
                fallback={pulse.confidence_trend?.trend === "insufficient_data" ? pulse.confidence_trend?.note : null}
                explainer="A team-level indicator only. Individual player scores are never shown."
                testid="tp-signal-confidence"
              />
            </div>

            {/* Motivation risk */}
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="tp-motivation-risk">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm text-neutral-400 mb-1">Quiet-player count (≥14 days inactive)</div>
                  <div className="text-3xl font-semibold text-emerald-300" data-testid="tp-quiet-count">
                    {pulse.motivation_risk?.quiet_count ?? 0}
                  </div>
                  <div className="text-xs text-neutral-500 mt-2 max-w-md">
                    {pulse.motivation_risk?.note ||
                     "This is a signal, not a diagnosis. Reduced activity doesn&apos;t imply a player intends to leave."}
                  </div>
                </div>
                {(pulse.motivation_risk?.quiet_count || 0) > 0 && (
                  <Button
                    variant="outline"
                    onClick={nudgeQuiet}
                    disabled={busy}
                    className="border-neutral-700 shrink-0"
                    data-testid="tp-nudge-quiet-btn"
                  >
                    Send private nudges
                  </Button>
                )}
              </div>
            </div>

            {/* Themes */}
            {(pulse.themes || []).length > 0 && (
              <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="tp-themes">
                <h2 className="text-sm text-neutral-400 mb-3">Broadly-shared themes ({`≥${pulse.privacy_floor?.k_anonymity_min || 5} players`})</h2>
                <ul className="space-y-1">
                  {pulse.themes.map((t, i) => (
                    <li key={i} className="text-sm flex justify-between border-b border-neutral-800 last:border-0 py-1.5" data-testid={`tp-theme-${i}`}>
                      <span className="capitalize text-neutral-200">{t.theme}</span>
                      <span className="text-xs text-neutral-500">{t.player_count} players</span>
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-neutral-500 mt-3">Themes are suppressed unless shared by enough players to preserve anonymity.</div>
              </div>
            )}

            {/* Digest preferences */}
            {prefs && (
              <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="tp-digest-prefs">
                <h2 className="text-base font-medium mb-1">Weekly digest</h2>
                <div className="text-xs text-neutral-500 mb-3">Sent Monday mornings. Suppressed automatically if privacy thresholds aren&apos;t met.</div>
                <div className="flex items-center gap-4">
                  <label className="text-sm flex items-center gap-2">
                    <input type="checkbox" checked={!!prefs.enrolled}
                           onChange={(e) => togglePref("enrolled", e.target.checked)}
                           className="accent-emerald-500"
                           data-testid="tp-digest-enrolled-toggle" />
                    Subscribed
                  </label>
                  <label className="text-sm flex items-center gap-2">
                    <input type="checkbox" checked={(prefs.channels || []).includes("email")}
                           disabled={!prefs.enrolled}
                           onChange={(e) => {
                             const next = new Set(prefs.channels || ["in_app"]);
                             if (e.target.checked) next.add("email"); else next.delete("email");
                             togglePref("channels", [...next]);
                           }}
                           className="accent-emerald-500"
                           data-testid="tp-digest-email-toggle" />
                    Also send by email
                  </label>
                </div>
              </div>
            )}
          </>
        )}

        {/* Refresh */}
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={refresh} disabled={busy}
                  className="border-neutral-700" data-testid="tp-refresh-btn">
            {busy ? "Refreshing…" : "Refresh"}
          </Button>
        </div>

        <div className="text-xs text-neutral-500 mt-8 leading-relaxed">
          Team Pulse only shows team-level signals. Player names, individual scores, message content, video content and report bodies are never shown to coaches. Every trend indicator shows its comparison period; nothing here implies causation or prediction of any single player&apos;s behaviour.
        </div>
      </div>
    </div>
  );
}

function SignalCard({ title, metric, band, trend, comparePeriod, explainer, fallback, testid }) {
  const bandColor = band === "high" ? "text-emerald-400" :
                    band === "moderate" ? "text-amber-400" :
                    band === "low" ? "text-red-400" : "text-neutral-500";
  const trendArrow = trend === "up" ? "↗" : trend === "down" ? "↘" : trend === "flat" ? "→" : "";
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5" data-testid={testid}>
      <div className="text-xs text-neutral-500 mb-1">{title}</div>
      {metric && <div className="text-lg text-neutral-100 mb-1">{metric}</div>}
      <div className="flex items-baseline gap-3">
        {band && <span className={`text-base capitalize ${bandColor}`}>{band} band</span>}
        {trend && (
          <span className="text-xs text-neutral-400">
            {trendArrow} {trend} {comparePeriod && <span className="text-neutral-600">({comparePeriod})</span>}
          </span>
        )}
      </div>
      {fallback && <div className="text-xs text-neutral-500 mt-2">{fallback}</div>}
      {explainer && <div className="text-xs text-neutral-500 mt-2">{explainer}</div>}
    </div>
  );
}

function ErrorBox({ children }) {
  return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6 text-center text-neutral-400">{children}</div>;
}
